import { LocalParticipant, RemoteParticipant, TrackPublication } from 'livekit-client';

import '@livekit/components-styles';

import { TileDescriptor, TileTypeEnum } from './utils';

export const WAITING_PLACEHOLDER = 'waiting-placeholder';
export const HIDDEN_PARTICIPANTS = 'hidden-participants';

export const USER_CHOICES = {
  videoInput: 'livekit_videoinput',
  audioInput: 'livekit_audioinput',
  audioOutput: 'livekit_output',
  videoDeviceId: 'livekit_video_device_id',
  audioDeviceId: 'livekit_audio_device_id',
  audioOutputId: 'livekit_audio_output_id',
};

export type LivekitParticipant = {
  name: string;
  userId?: string;
  isScreenSharing?: boolean;
  id: string;
  publication?: TrackPublication;
  isLocal: boolean;
};

export const countWithProperty = <T>(array: T[], property: keyof T) =>
  array.reduce((count, item) => count + (item[property] ? 1 : 0), 0);

interface GetGridItemsProps {
  participants: LivekitParticipant[];
  hasPlaceholder: boolean;
  hasHiddenTilesItem: boolean;
}

export const getGridItems = ({ participants, hasPlaceholder, hasHiddenTilesItem }: GetGridItemsProps) => {
  const participantCount = participants.length - countWithProperty(participants, 'isScreenSharing');

  const tileDescriptors: TileDescriptor[] = participants.map((participant) => {
    const { id, isLocal, isScreenSharing } = participant;

    return {
      id,
      type: TileTypeEnum.PARTICIPANT,
      participant,
      isLocal,
      isScreenSharing,
    };
  });

  if (hasHiddenTilesItem) {
    tileDescriptors.push({
      id: HIDDEN_PARTICIPANTS,
      type: TileTypeEnum.HIDDEN_PARTICIPANTS,
    });
  }

  if (participantCount < 2 && hasPlaceholder) {
    tileDescriptors.push({
      id: WAITING_PLACEHOLDER,
      type: TileTypeEnum.PLACEHOLDER,
      replace: true,
    });
  }

  return tileDescriptors;
};

export const findParticipantById = ({ identity }: RemoteParticipant | LocalParticipant, id: string) => {
  const [realId] = id.split('_');
  return realId === identity;
};

export const getIsScreenShareTile = (id: string) => id.split('_').includes('screen');

export const hashPassphrase = async (passphrase: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(passphrase);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');

  return hashHex;
};

export const withWhiteboardItem = (
  base: TileDescriptor[],
  { isOpen, roomId, userId }: { isOpen: boolean; roomId: string; userId: string }
): TileDescriptor[] => {
  if (!isOpen) return base;

  const wb: TileDescriptor = {
    id: `whiteboard:${roomId}:${userId}`,
    type: TileTypeEnum.WHITEBOARD,
  };

  return [wb, ...base.filter((t) => t.type !== TileTypeEnum.WHITEBOARD)];
};
